/**
 * @callback HandleApiErrorCallback
 * @param {string} endpointName
 * @param {ApiErrorResponse} errorResponse
 */
/**
 * @typedef {ApiErrorResponse} ApiResponse
 * @property {boolean} ok
 * @property {any} data
 */
/**
 * @typedef ApiErrorResponse
 * @property {number} status
 * @property {Error?} exception
 * @property {any?} errorData
 */

export default class ApiClientCommon {
  /**
   * @param {string} baseUrl
   * @param {HandleApiErrorCallback?} handleApiError
   */
  constructor(baseUrl, handleApiError) {
    this.#baseUrl = baseUrl;
    this.#handleApiError = handleApiError;
  }

  /** @type {string} */
  #baseUrl;

  /** @type {HandleApiErrorCallback} */
  #handleApiError;

  /**
   * @param {string} path
   * @return {string}
   */
  #getEndpointUrl(path) {
    return this.#baseUrl ? `${this.#baseUrl}${path}` : path;
  }

  /**
   * @typedef FetchOptionsCommon
   * @property {string} path
   * @property {Headers?} headers
   * @property {boolean=true} includeResponseBody
   * @property {boolean=false} expectJsonResponse
   */
  /**
   * @typedef {FetchOptionsCommon} FetchOptions
   * @property {"POST"|"GET"} method
   * @property {string|URLSearchParams|undefined} body
   */
  /**
   * @param {FetchOptions} options
   * @return {Promise<ApiResponse>}
   */
  // eslint-disable-next-line class-methods-use-this
  async #fetch(options) {
    /** @type ApiResponse */
    const result = {
      ok: false,
      status: 499,
    };

    let response;
    try {
      const url = this.#getEndpointUrl(options.path);
      const init = {
        method: options.method,
        headers: options.headers,
        body: options.body ? options.body : undefined,
      };
      response = await fetch(url, init);
      result.status = response.status;
      let responseData = null;
      if (options.includeResponseBody ?? true) {
        responseData = options.expectJsonResponse || response.headers.get("Content-Type") === "application/json"
          ? await response.json()
          : await response.text();
      }
      if (response.ok) {
        result.data = responseData ?? true;
        result.ok = true;
      } else {
        result.data = false;
        result.errorData = responseData;
      }
    } catch (error) {
      if (!result.status) result.status = 499;
      result.exception = error;
    }

    return result;
  }

  /**
   * @param {string} endpointName
   * @param {ApiErrorResponse} response
   * */
  #handleError(endpointName, response) {
    if (!this.#handleApiError) {
      // eslint-disable-next-line no-console
      console.log(`unhandled api error: ${JSON.stringify(response)}`);
    } else {
      this.#handleApiError(endpointName, response);
    }
  }

  /**
   * @typedef {FetchOptionsCommon} DoGetOptions
   * @property {string} name
   */
  /** @param {DoGetOptions} options */
  async doGet(options) {
    const response = await this.#fetch(Object.assign(options, {
      method: "GET",
    }));
    if (!response.ok) {
      this.#handleError(options.name, response);
      return false;
    }
    return response.data;
  }

  /**
   * @typedef {FetchOptionsCommon} DoJsonPostOptions
   * @property {string} name
   * @property {object} body
   */
  /** @param {DoJsonPostOptions} options */
  async doJsonPost(options) {
    const response = await this.#fetch(Object.assign(options, {
      method: "POST",
      body: JSON.stringify(options.body),
      headers: this.#getHeaders(true),
    }));
    if (!response.ok) {
      this.#handleError(options.name, response);
      return false;
    }
    return response.data;
  }

  /** @typedef {Object.<string, string>} FormParams */
  /**
   * @typedef {FetchOptionsCommon} DoFormPostOptions
   * @property {string} name
   * @property {FormParams} formParams
   */
  /** @param {DoFormPostOptions} options */
  async doFormPost(options) {
    const response = await this.#fetch(Object.assign(options, {
      method: "POST",
      body: new URLSearchParams(options.formParams),
      headers: this.#getHeaders(false),
    }));
    if (!response.ok) {
      this.#handleError(options.name, response);
      return false;
    }
    return response.data;
  }

  /**
   * @param {boolean} isJsonPost
   * @return Headers
   * */
  #getHeaders(isJsonPost) {
    const headers = new Headers();
    headers.set("content-type", isJsonPost ? "application/json" : "application/x-www-form-urlencoded");
    return headers;
  }
}
